import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { hasWildcardAccess } from "@/lib/permissions";
import {
    X,
    Loader2,
    ChevronRight,
    Building2,
    Users,
    Layers,
    Monitor,
    Radio,
    Scissors,
    BarChart3,
    Wand2,
    Search,
    PenLine,
    Megaphone,
    Compass,
    CreditCard,
    Settings,
    Link2,
} from "lucide-react";
import { toast } from "sonner";
import { DecodedPermission, UserRequest } from "@/types/profile";
import { authApi } from "@/api/auth";
import { profileApi } from "@/api/profile";
import { rpcApi, RPCResponse } from "@/api/rpc";
import { Business, Team } from "@/types/requestAccess";
import { UserMeData } from "@/types/profile";
import {
    mapAccessLevelToLevel,
    parseServiceFromRequest,
    formatDisplayName,
    matchesAccessFilter,
} from "@/utils/permissionHelpers";
import { SERVICE_TYPES, LEVEL_LABEL, type PermissionLevel } from "@/utils/constants";

// ============================================================================
// TOOL DEFINITIONS
// ============================================================================

type RequestLevel = PermissionLevel;

interface ToolEntry {
    id: string;
    name: string;
    description: string;
    url: string;
    icon: React.ElementType;
    iconBg: string;
    supportsRead: boolean;
    requires?: readonly string[];
}

const TOOLS: readonly ToolEntry[] = [
    {
        id: "cms",
        name: "OTT CMS",
        description: "On-demand content",
        url: "https://app.sli.ke/",
        icon: Monitor,
        iconBg: "bg-violet-500/10 text-violet-600",
        supportsRead: true,
    },
    {
        id: "live",
        name: "Live CMS",
        description: "Live streaming",
        url: "https://cms.sli.ke/",
        icon: Radio,
        iconBg: "bg-rose-500/10 text-rose-600",
        supportsRead: true,
    },
    {
        id: "analytics",
        name: "Analytics",
        description: "Data & metrics",
        url: "https://data.slike.in/",
        icon: BarChart3,
        iconBg: "bg-emerald-500/10 text-emerald-600",
        supportsRead: true,
    },
    {
        id: "admin_console",
        name: "Admin Console",
        description: "Configure & manage account",
        url: "https://admin.sli.ke/",
        icon: Settings,
        iconBg: "bg-slate-500/10 text-slate-600",
        supportsRead: false,
    },
    {
        id: "optimus",
        name: "Optimus",
        description: "AI assisted videos",
        url: "https://optimus.sli.ke/",
        icon: Wand2,
        iconBg: "bg-sky-500/10 text-sky-600",
        supportsRead: false,
        requires: ["cms"] as const,
    },
    {
        id: "chopper",
        name: "Chopper",
        description: "Highlights & Reels",
        url: "https://chopper.sli.ke/",
        icon: Scissors,
        iconBg: "bg-amber-500/10 text-amber-600",
        supportsRead: false,
        requires: ["cms"] as const,
    },
    {
        id: "billing",
        name: "Billing Dashboard",
        description: "Manage billing & invoices",
        url: "https://billing.sli.ke/",
        icon: CreditCard,
        iconBg: "bg-lime-500/10 text-lime-600",
        supportsRead: false,
    },
    {
        id: "compass",
        name: "Compass AI",
        description: "AI driven project management",
        url: "https://compass.sli.ke/",
        icon: Compass,
        iconBg: "bg-blue-500/10 text-blue-600",
        supportsRead: true,
    },
    {
        id: "ad_manager",
        name: "Ad Manager",
        description: "Manage advertisements",
        url: "https://smanager.sli.ke/",
        icon: Megaphone,
        iconBg: "bg-orange-500/10 text-orange-600",
        supportsRead: false,
    },
] as const;

const WRITE_STRIP = "bg-violet-500";
const WRITE_BG = "bg-violet-500/[0.04]";
const READ_STRIP = "bg-slate-400";
const READ_BG = "bg-slate-500/[0.03]";
const ADMIN_STRIP = "bg-purple-500";
const ADMIN_BG = "bg-purple-500/[0.04]";

// Maps tool id → available request levels and default when first clicked
const TOOL_PERMISSION_CONFIG: Record<string, { availableLevels: RequestLevel[]; defaultLevel: RequestLevel }> = {
    cms:               { availableLevels: ["r", "w", "*"], defaultLevel: "w" },
    live:              { availableLevels: ["r", "w", "*"], defaultLevel: "w" },
    chopper:           { availableLevels: ["w"],           defaultLevel: "w" },
    optimus:           { availableLevels: ["w"],           defaultLevel: "w" },
    analytics:         { availableLevels: ["r", "w"],      defaultLevel: "r" },
    compass:           { availableLevels: ["r", "w"],      defaultLevel: "w" },
    billing:           { availableLevels: ["*"],           defaultLevel: "*" },
    admin_console:     { availableLevels: ["*"],           defaultLevel: "*" },
    ad_manager:        { availableLevels: ["w"],           defaultLevel: "w" },
};

// ============================================================================
// STAGED PERMISSION ENTRY
// ============================================================================

interface StagedEntry {
    business_id: string;
    team_id: string;
    tool_id: string;
    level: RequestLevel;
}

// ============================================================================
// TYPES
// ============================================================================

export interface PermissionItem {
    id: string;
    permission: string;
    business: string;
    business_name: string;
    product: string;
    team_name: string;
    service: string;
    services: string[];
    level: PermissionLevel;
    access_level: string;
    isRequestPending?: boolean;
}

export interface AvailablePermission {
    value: string;
    label: string;
    business: string;
    product: string;
    service: string;
}

interface ManageAccessProps {
    permissions: string[];
    decoded_permissions?: DecodedPermission[];
    user_req?: UserRequest[];
    availablePermissions?: AvailablePermission[];
    onUpdatePermission?: (permissionId: string, newLevel: PermissionLevel) => Promise<void>;
    onAddPermissions?: (permissionIds: string[]) => Promise<void>;
    onRemovePermission?: (permissionId: string) => Promise<void>;
    onRequestPermission?: (permission: AvailablePermission) => Promise<void>;
    onCancelRequest?: (requestId: string) => Promise<void>;
    onDone?: () => Promise<void>;
    isProfile?: boolean;
    userId?: string | null;
    businessNameMapping?: Record<string, string>;
    teamNameMapping?: Record<string, string>;
    autoOpenDialog?: boolean;
    defaultTeam?: string;
}

const SHOW_PERMISSION_COUNT = 6;

interface GroupEntry {
    id: string;
    nid?: number;
    title: string;
    name?: string;
    description?: string;
    permissions?: string[];
    business?: string[];
    status?: number;
}

// ============================================================================
// TOOL CARD COMPONENT
// ============================================================================

interface ToolCardProps {
    tool: ToolEntry;
    level: RequestLevel | "";
    justGranted: boolean;
    onCardClick: (toolId: string) => void;
    onLevelTab: (toolId: string, level: RequestLevel, e: React.MouseEvent) => void;
    disabled: boolean;
    alreadyHasAccess: boolean;
    isPendingRequest: boolean;
    grantedLevel?: PermissionLevel;
    pendingLevel?: PermissionLevel;
}

function ToolCard({ tool, level, justGranted, onCardClick, onLevelTab, disabled, alreadyHasAccess, isPendingRequest, grantedLevel, pendingLevel }: ToolCardProps) {
    const isActive = !!level;
    const config = TOOL_PERMISSION_CONFIG[tool.id];
    const availableLevels = config?.availableLevels ?? ["w"];

    const stripColor = level === "w" ? WRITE_STRIP : level === "r" ? READ_STRIP : level === "*" ? ADMIN_STRIP : "bg-transparent";
    const cardBg = level === "w" ? WRITE_BG : level === "r" ? READ_BG : level === "*" ? ADMIN_BG : "";

    // Granted cards: purple styling
    const grantedStrip = ADMIN_STRIP;
    const grantedBg = ADMIN_BG;

    const requiresNames = tool.requires
        ? tool.requires.map(reqId => TOOLS.find(t => t.id === reqId)?.name ?? reqId)
        : [];

    const Icon = tool.icon;

    if (alreadyHasAccess) {
        return (
            <div className={`relative rounded-lg border overflow-hidden flex flex-col select-none opacity-100 h-[110px] ${grantedBg} border-purple-400/40`}>
                {/* Granted accent strip */}
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${grantedStrip}`} />
                {/* Granted badge top-right */}
                <div className="absolute top-2 right-2">
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-700 border border-purple-400/30">Granted</span>
                </div>
                <div className="flex items-start gap-2.5 pl-3 pr-3 pt-3 pb-2 flex-1">
                    <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${tool.iconBg}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1 pr-12">
                        <p className="text-sm font-semibold leading-none">{tool.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{tool.description}</p>
                        {requiresNames.length > 0 && (
                            <div className="flex items-center gap-1 mt-1.5">
                                <Link2 className="h-2.5 w-2.5 text-muted-foreground/35 shrink-0" />
                                <span className="text-[9px] text-muted-foreground/35 whitespace-nowrap">Requires {requiresNames.join(", ")}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="px-2.5 pb-2 pt-0">
                    <div className="inline-flex rounded-md border border-border/60 overflow-hidden text-[9px] font-semibold">
                        {availableLevels.map((lvl, idx) => {
                            const isGrantedLevel = grantedLevel === lvl;
                            const activeStyle = lvl === "w"
                                ? "bg-violet-500 text-white"
                                : lvl === "*"
                                ? "bg-purple-600 text-white"
                                : "bg-slate-500 text-white";
                            return (
                                <span
                                    key={lvl}
                                    className={`px-2 py-[3px] cursor-default
                                        ${idx > 0 ? "border-l border-border/60" : ""}
                                        ${isGrantedLevel ? activeStyle : "bg-background text-muted-foreground"}
                                    `}
                                >
                                    {LEVEL_LABEL[lvl]}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    if (isPendingRequest) {
        return (
            <div className="relative rounded-lg border overflow-hidden flex flex-col select-none opacity-60 pointer-events-none h-[110px] border-border">
                {/* Requested badge top-right */}
                <div className="absolute top-2 right-2">
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 border border-amber-400/30">Requested</span>
                </div>
                <div className="flex items-start gap-2.5 pl-3 pr-3 pt-3 pb-2 flex-1">
                    <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${tool.iconBg}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1 pr-12">
                        <p className="text-sm font-semibold leading-none">{tool.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{tool.description}</p>
                        {requiresNames.length > 0 && (
                            <div className="flex items-center gap-1 mt-1.5">
                                <Link2 className="h-2.5 w-2.5 text-muted-foreground/35 shrink-0" />
                                <span className="text-[9px] text-muted-foreground/35 whitespace-nowrap">Requires {requiresNames.join(", ")}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="px-2.5 pb-2 pt-0">
                    <div className="inline-flex rounded-md border border-border/60 overflow-hidden text-[9px] font-semibold">
                        {availableLevels.map((lvl, idx) => {
                            const isPendingLevel = pendingLevel === lvl;
                            const activeStyle = lvl === "w"
                                ? "bg-violet-500 text-white"
                                : lvl === "*"
                                ? "bg-purple-600 text-white"
                                : "bg-slate-500 text-white";
                            return (
                                <span
                                    key={lvl}
                                    className={`px-2 py-[3px] cursor-default
                                        ${idx > 0 ? "border-l border-border/60" : ""}
                                        ${isPendingLevel ? activeStyle : "bg-background text-muted-foreground"}
                                    `}
                                >
                                    {LEVEL_LABEL[lvl]}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={() => !disabled && onCardClick(tool.id)}
            className={`relative rounded-lg border overflow-hidden flex flex-col transition-all select-none h-[110px]
                ${justGranted ? "border-violet-400 ring-2 ring-violet-300/50" : "border-border"}
                ${isActive ? cardBg : ""}
                ${disabled ? "opacity-50 pointer-events-none" : "cursor-pointer hover:border-muted-foreground/30"}
            `}
        >
            {/* Accent strip */}
            <div className={`absolute left-0 top-0 bottom-0 w-[3px] transition-all ${stripColor}`} />

            <div className="flex items-start gap-2.5 pl-3 pr-3 pt-3 pb-2 flex-1">
                <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${tool.iconBg}`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-none">{tool.name}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{tool.description}</p>
                    {requiresNames.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5">
                            <Link2 className="h-2.5 w-2.5 text-muted-foreground/35 shrink-0" />
                            <span className="text-[9px] text-muted-foreground/35 whitespace-nowrap">
                                Requires {requiresNames.join(", ")}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Level tabs — always rendered for stable card height */}
            <div className={`px-2.5 pb-2 pt-0 ${!isActive ? "opacity-0" : ""}`}>
                <div className="inline-flex rounded-md border border-border/60 overflow-hidden text-[9px] font-semibold">
                    {availableLevels.map((lvl, idx) => {
                        const isCurrentLevel = level === lvl;
                        const activeStyle = lvl === "w"
                            ? "bg-violet-500 text-white"
                            : lvl === "*"
                            ? "bg-purple-600 text-white"
                            : "bg-slate-500 text-white";
                        const inactiveStyle = "bg-background text-muted-foreground hover:bg-muted/60";
                        return (
                            <button
                                key={lvl}
                                type="button"
                                onClick={(e) => isActive ? onLevelTab(tool.id, lvl, e) : e.stopPropagation()}
                                className={`px-2 py-[3px] transition-colors
                                    ${idx > 0 ? "border-l border-border/60" : ""}
                                    ${isActive && isCurrentLevel ? activeStyle : inactiveStyle}
                                    ${availableLevels.length === 1 ? "cursor-default" : ""}
                                `}
                                tabIndex={isActive ? 0 : -1}
                            >
                                {LEVEL_LABEL[lvl]}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// PERMISSION SELECTOR DIALOG BODY
// ============================================================================

interface PermissionSelectorBodyProps {
    businesses: Business[];
    staged: StagedEntry[];
    onStagedChange: (next: StagedEntry[]) => void;
    existingPermissions: string[];
    pendingRequests: UserRequest[];
    defaultTeam?: string;
}

function PermissionSelectorBody({
    businesses,
    staged,
    onStagedChange,
    existingPermissions,
    pendingRequests,
    defaultTeam,
}: PermissionSelectorBodyProps) {
    const [selectedBizId, setSelectedBizId] = useState<string>("");
    const [selectedTeamId, setSelectedTeamId] = useState<string>("");
    const [expandedBizIds, setExpandedBizIds] = useState<Set<string>>(new Set());
    const [treeSearch, setTreeSearch] = useState("");
    const [justGranted, setJustGranted] = useState<Set<string>>(new Set());
    const isManualEdit = useRef(false);

    // Initialize selection on businesses load
    useEffect(() => {
        if (businesses.length === 0) return;
        const sorted = businesses;

        // Try to find default team's business
        let initialBizId = sorted[0]?.id ?? "";
        let initialTeamId = "";

        if (defaultTeam) {
            const normalizedDefault = defaultTeam.trim().toLowerCase();
            for (const biz of sorted) {
                const match = biz.teams?.find(
                    t => t.id.toLowerCase() === normalizedDefault || t.name.toLowerCase() === normalizedDefault
                );
                if (match) {
                    initialBizId = biz.id;
                    initialTeamId = match.id;
                    break;
                }
            }
        }

        setSelectedBizId(initialBizId);
        setSelectedTeamId(initialTeamId);
        setExpandedBizIds(new Set(sorted.map(b => b.id)));
    }, [businesses, defaultTeam]);

    // Sync selection when staged changes externally
    useEffect(() => {
        if (isManualEdit.current) { isManualEdit.current = false; return; }
        if (staged.length > 0) {
            const first = staged[0];
            setSelectedBizId(first.business_id);
            setSelectedTeamId(first.team_id);
            setExpandedBizIds(prev => { const s = new Set(prev); s.add(first.business_id); return s; });
        }
    }, [staged]);

    const stagedMap = useMemo(() => {
        const m = new Map<string, RequestLevel>();
        for (const e of staged) {
            m.set(`${e.business_id}:${e.team_id}:${e.tool_id}`, e.level);
        }
        return m;
    }, [staged]);

    const grantedKeys = useMemo(() => {
        const keys = new Map<string, PermissionLevel>();
        for (const perm of existingPermissions) {
            if (perm === "*.*.*.*") {
                // super admin — all granted
                TOOLS.forEach(t => keys.set(`${selectedBizId}:${selectedTeamId}:${t.id}`, "*"));
                break;
            }
            const parts = perm.split(".");
            if (parts.length >= 3) {
                const biz = parts[parts.length - 4] || "";
                const team = parts[parts.length - 3] || "";
                const svc = parts[parts.length - 2] || "";
                const lvl = mapAccessLevelToLevel(parts[parts.length - 1] || "r");
                if (biz === selectedBizId && team === selectedTeamId) {
                    if (svc === "*") {
                        // Wildcard service — user has access to all services for this team
                        TOOLS.forEach(t => keys.set(`${biz}:${team}:${t.id}`, lvl));
                    } else {
                        keys.set(`${biz}:${team}:${svc}`, lvl);
                    }
                }
            }
        }
        return keys;
    }, [existingPermissions, selectedBizId, selectedTeamId]);

    // Build map of pending request keys → level for selected team
    const pendingKeys = useMemo(() => {
        const keys = new Map<string, PermissionLevel>();
        for (const req of pendingRequests) {
            if (req.business === selectedBizId && req.team === selectedTeamId) {
                const parsed = parseServiceFromRequest(req.service);
                for (const { name, level } of parsed) {
                    keys.set(`${req.business}:${req.team}:${name}`, level);
                }
            }
        }
        return keys;
    }, [pendingRequests, selectedBizId, selectedTeamId]);

    const getCount = useCallback((bizId: string, teamId?: string) => {
        let count = 0;
        for (const [key] of stagedMap) {
            if (teamId ? key.startsWith(`${bizId}:${teamId}:`) : key.startsWith(`${bizId}:`)) count++;
        }
        return count;
    }, [stagedMap]);

    const getLevel = useCallback((toolId: string): RequestLevel | "" => {
        if (!selectedTeamId) return "";
        return stagedMap.get(`${selectedBizId}:${selectedTeamId}:${toolId}`) || "";
    }, [stagedMap, selectedBizId, selectedTeamId]);

    const setPermission = useCallback((toolId: string, level: RequestLevel) => {
        if (!selectedTeamId) return;
        const current = getLevel(toolId);
        let next: StagedEntry[];
        if (current === level) {
            next = staged.filter(p => !(p.business_id === selectedBizId && p.team_id === selectedTeamId && p.tool_id === toolId));
        } else {
            next = staged.filter(p => !(p.business_id === selectedBizId && p.team_id === selectedTeamId && p.tool_id === toolId));
            next.push({ business_id: selectedBizId, team_id: selectedTeamId, tool_id: toolId, level });
        }
        isManualEdit.current = true;
        onStagedChange(next);
    }, [selectedBizId, selectedTeamId, getLevel, staged, onStagedChange]);

    const handleCardClick = useCallback((toolId: string) => {
        if (!selectedTeamId) return;
        const current = getLevel(toolId);
        const tool = TOOLS.find(t => t.id === toolId)!;
        const config = TOOL_PERMISSION_CONFIG[toolId];
        const defaultLevel: RequestLevel = config?.defaultLevel ?? "w";

        if (current) {
            // Revoke — cascade-revoke dependents
            const dependentIds = TOOLS.filter(t => t.requires?.includes(toolId)).map(t => t.id);
            const toRemove = new Set([toolId, ...dependentIds]);
            const next = staged.filter(p => !(p.business_id === selectedBizId && p.team_id === selectedTeamId && toRemove.has(p.tool_id)));
            isManualEdit.current = true;
            onStagedChange(next);
        } else {
            // Grant default level — auto-grant unmet requirements
            const requiredIds: string[] = tool.requires ? [...tool.requires] : [];
            const toAutoGrant = requiredIds.filter(reqId => !getLevel(reqId) && !grantedKeys.has(`${selectedBizId}:${selectedTeamId}:${reqId}`));
            const toReplace = new Set([toolId, ...toAutoGrant]);
            const next = staged.filter(p => !(p.business_id === selectedBizId && p.team_id === selectedTeamId && toReplace.has(p.tool_id)));
            next.push({ business_id: selectedBizId, team_id: selectedTeamId, tool_id: toolId, level: defaultLevel });
            for (const reqId of toAutoGrant) {
                const reqDefault = TOOL_PERMISSION_CONFIG[reqId]?.defaultLevel ?? "w";
                next.push({ business_id: selectedBizId, team_id: selectedTeamId, tool_id: reqId, level: reqDefault });
            }
            if (toAutoGrant.length > 0) {
                setJustGranted(new Set(toAutoGrant));
                setTimeout(() => setJustGranted(new Set()), 1200);
            }
            isManualEdit.current = true;
            onStagedChange(next);
        }
    }, [selectedTeamId, selectedBizId, getLevel, staged, onStagedChange]);

    const handleLevelTab = useCallback((toolId: string, level: RequestLevel, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!selectedTeamId) return;
        setPermission(toolId, level);
    }, [selectedTeamId, setPermission]);

    const clearTeamPermissions = useCallback(() => {
        if (!selectedTeamId) return;
        const next = staged.filter(p => !(p.business_id === selectedBizId && p.team_id === selectedTeamId));
        isManualEdit.current = true;
        onStagedChange(next);
    }, [selectedBizId, selectedTeamId, staged, onStagedChange]);

    const requestAllWrite = useCallback(() => {
        if (!selectedTeamId) return;
        const next = [
            ...staged.filter(p => !(p.business_id === selectedBizId && p.team_id === selectedTeamId)),
            ...TOOLS.map(tool => {
                const defaultLevel = TOOL_PERMISSION_CONFIG[tool.id]?.defaultLevel ?? "w";
                return { business_id: selectedBizId, team_id: selectedTeamId, tool_id: tool.id, level: defaultLevel };
            }),
        ];
        isManualEdit.current = true;
        onStagedChange(next);
    }, [selectedBizId, selectedTeamId, staged, onStagedChange]);

    const removeStagedEntry = useCallback((bizId: string, teamId: string, toolId: string) => {
        // Cascade-revoke dependents in the same biz/team, then mark as manual so selection doesn't jump
        const dependentIds = TOOLS.filter(t => t.requires?.includes(toolId)).map(t => t.id);
        const toRemove = new Set([toolId, ...dependentIds]);
        const next = staged.filter(p => !(p.business_id === bizId && p.team_id === teamId && toRemove.has(p.tool_id)));
        isManualEdit.current = true;
        onStagedChange(next);
    }, [staged, onStagedChange]);

    const toggleBiz = (bizId: string) => {
        setExpandedBizIds(prev => {
            const s = new Set(prev);
            if (s.has(bizId)) s.delete(bizId); else s.add(bizId);
            return s;
        });
    };

    // Build a set of biz+team pairs that already have permissions
    const accessedPairs = useMemo(() => {
        const pairs = new Set<string>();
        for (const perm of existingPermissions) {
            if (perm === "*.*.*.*") continue;
            const parts = perm.split(".");
            if (parts.length >= 3) {
                const biz = parts[parts.length - 4] || "";
                const team = parts[parts.length - 3] || "";
                if (biz && team) pairs.add(`${biz}:${team}`);
            }
        }
        return pairs;
    }, [existingPermissions]);

    const filteredBusinesses = useMemo(() => {
        const base = !treeSearch.trim()
            ? businesses
            : businesses
                .map(biz => {
                    const bizMatch = biz.name.toLowerCase().includes(treeSearch.toLowerCase());
                    const matchingTeams = biz.teams?.filter(t => t.name.toLowerCase().includes(treeSearch.toLowerCase())) ?? [];
                    if (bizMatch) return biz;
                    if (matchingTeams.length > 0) return { ...biz, teams: matchingTeams };
                    return null;
                })
                .filter(Boolean) as Business[];

        const teamCountSort = (a: Team, b: Team) =>
            (b.last_7d_video_count ?? 0) - (a.last_7d_video_count ?? 0) ||
            (b.video_count ?? 0) - (a.video_count ?? 0) ||
            (b.last_video_ct ?? 0) - (a.last_video_ct ?? 0);

        // Sort: businesses with existing access float to top, teams with access float to top within biz
        return base.map(biz => {
            const sortedTeams = [...(biz.teams ?? [])].sort((a, b) => {
                const aHas = accessedPairs.has(`${biz.id}:${a.id}`);
                const bHas = accessedPairs.has(`${biz.id}:${b.id}`);
                const aDefault = defaultTeam && (a.id.toLowerCase() === defaultTeam.toLowerCase() || a.name.toLowerCase() === defaultTeam.toLowerCase());
                const bDefault = defaultTeam && (b.id.toLowerCase() === defaultTeam.toLowerCase() || b.name.toLowerCase() === defaultTeam.toLowerCase());
                if (aDefault && !bDefault) return -1;
                if (!aDefault && bDefault) return 1;
                if (aHas && !bHas) return -1;
                if (!aHas && bHas) return 1;
                return teamCountSort(a, b);
            });
            return { ...biz, teams: sortedTeams };
        }).sort((a, b) => {
            const aHas = a.teams?.some(t => accessedPairs.has(`${a.id}:${t.id}`));
            const bHas = b.teams?.some(t => accessedPairs.has(`${b.id}:${t.id}`));
            const aIsDefault = defaultTeam && a.teams?.some(t =>
                t.id.toLowerCase() === defaultTeam.toLowerCase() || t.name.toLowerCase() === defaultTeam.toLowerCase()
            );
            const bIsDefault = defaultTeam && b.teams?.some(t =>
                t.id.toLowerCase() === defaultTeam.toLowerCase() || t.name.toLowerCase() === defaultTeam.toLowerCase()
            );
            if (aIsDefault && !bIsDefault) return -1;
            if (!aIsDefault && bIsDefault) return 1;
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
            const aMaxCount = Math.max(...(a.teams?.map(t => t.last_7d_video_count ?? 0) ?? [0]));
            const bMaxCount = Math.max(...(b.teams?.map(t => t.last_7d_video_count ?? 0) ?? [0]));
            return bMaxCount - aMaxCount;
        });
    }, [treeSearch, businesses, accessedPairs, defaultTeam]);

    const selectedBiz = businesses.find(b => b.id === selectedBizId);
    const selectedTeam = selectedBiz?.teams.find(t => t.id === selectedTeamId);
    const teamStagedCount = getCount(selectedBizId, selectedTeamId);

    return (
        <>
        <div className="flex gap-4 items-start">
            {/* LHS: Business/Team tree */}
            <div className="w-[280px] shrink-0 border rounded-xl overflow-hidden flex flex-col">
                <div className="px-2 pt-2 pb-2 border-b">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            value={treeSearch}
                            onChange={e => setTreeSearch(e.target.value)}
                            placeholder="Search teams..."
                            className="h-8 pl-8 text-xs border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                        />
                    </div>
                </div>
                <div className="p-1.5 overflow-y-auto max-h-[350px]">
                    {filteredBusinesses.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                            <p className="text-xs">No teams found</p>
                        </div>
                    )}
                    {filteredBusinesses.map(biz => {
                        const bizCount = getCount(biz.id);
                        const isDefaultBiz = defaultTeam && biz.teams?.some(t =>
                            t.id.toLowerCase() === defaultTeam.toLowerCase() ||
                            t.name.toLowerCase() === defaultTeam.toLowerCase()
                        );
                        return (
                            <Collapsible
                                key={biz.id}
                                open={expandedBizIds.has(biz.id)}
                                onOpenChange={() => toggleBiz(biz.id)}
                            >
                                <CollapsibleTrigger asChild>
                                    <button className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-muted/80 transition-colors text-left ${isDefaultBiz ? "bg-primary/5 border border-primary/20" : ""}`}>
                                        <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expandedBizIds.has(biz.id) ? "rotate-90" : ""}`} />
                                        <Building2 className={`h-4 w-4 ${isDefaultBiz ? "text-primary" : "text-muted-foreground"}`} />
                                        <span className={`text-sm font-medium flex-1 ${isDefaultBiz ? "text-primary" : ""}`}>{biz.name}</span>
                                        {isDefaultBiz && (
                                            <span className="text-[9px] font-semibold text-primary/70 bg-primary/10 px-1.5 py-0.5 rounded-full mr-1">default</span>
                                        )}
                                        {bizCount > 0 && (
                                            <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{bizCount}</Badge>
                                        )}
                                    </button>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <div className="ml-5 border-l pl-2 space-y-0.5 mb-1">
                                        {biz.teams?.filter(t => t.archive !== 1).map(team => {
                                            const teamCount = getCount(biz.id, team.id);
                                            const isActive = selectedBizId === biz.id && selectedTeamId === team.id;
                                            const isDefaultTeam = defaultTeam && (
                                                team.id.toLowerCase() === defaultTeam.toLowerCase() ||
                                                team.name.toLowerCase() === defaultTeam.toLowerCase()
                                            );
                                            return (
                                                <button
                                                    key={team.id}
                                                    onClick={() => { setSelectedBizId(biz.id); setSelectedTeamId(team.id); }}
                                                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-colors text-sm
                                                        ${isActive ? "bg-primary/10 text-primary font-medium"
                                                            : isDefaultTeam ? "bg-primary/5 text-primary/80 hover:bg-primary/10"
                                                            : "hover:bg-muted/60 text-foreground"}`}
                                                >
                                                    <Users className={`h-3.5 w-3.5 shrink-0 ${isDefaultTeam ? "text-primary/70" : "text-muted-foreground"}`} />
                                                    <span className="flex-1 truncate">{team.name}</span>
                                                    {isDefaultTeam && (
                                                        <span className="text-[9px] font-semibold text-primary/70 bg-primary/10 px-1.5 py-0.5 rounded-full">default</span>
                                                    )}
                                                    {teamCount > 0 && (
                                                        <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5
                                                            ${isActive ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                                                            {teamCount}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        );
                    })}
                </div>
            </div>

            {/* RHS: Tool cards */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-3">
                    {selectedTeamId ? (
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{selectedBiz?.name}</span>
                            <span className="text-sm text-muted-foreground">/</span>
                            <span className="text-sm font-semibold">{selectedTeam?.name}</span>
                        </div>
                    ) : (
                        <p className="text-xs text-muted-foreground">Select a team to stage tool access</p>
                    )}
                    {selectedTeamId && (
                        <div className="flex items-center gap-1.5">
                            <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={requestAllWrite}>
                                <PenLine className="h-3 w-3 mr-1" />
                                Request All
                            </Button>
                            {teamStagedCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[11px] px-2 text-muted-foreground hover:text-destructive"
                                    onClick={clearTeamPermissions}
                                >
                                    <X className="h-3 w-3 mr-1" />
                                    Clear
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {!selectedTeamId ? (
                    <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground border rounded-xl border-dashed">
                        <Layers className="h-10 w-10 mb-3 opacity-30" />
                        <p className="text-sm">Select a team from the tree</p>
                        <p className="text-xs opacity-60 mt-1">to stage tool access requests</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-2.5">
                        {TOOLS.map(tool => (
                            <ToolCard
                                key={tool.id}
                                tool={tool}
                                level={getLevel(tool.id)}
                                justGranted={justGranted.has(tool.id)}
                                onCardClick={handleCardClick}
                                onLevelTab={handleLevelTab}
                                disabled={!selectedTeamId}
                                alreadyHasAccess={grantedKeys.has(`${selectedBizId}:${selectedTeamId}:${tool.id}`)}
                                isPendingRequest={pendingKeys.has(`${selectedBizId}:${selectedTeamId}:${tool.id}`)}
                                grantedLevel={grantedKeys.get(`${selectedBizId}:${selectedTeamId}:${tool.id}`)}
                                pendingLevel={pendingKeys.get(`${selectedBizId}:${selectedTeamId}:${tool.id}`)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>

        {/* ── Selected section ── */}
        {staged.length > 0 && (
            <div className="mt-4 pt-4 border-t">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
                    Selected ({staged.length})
                </p>
                <div className="grid grid-cols-4 gap-2">
                    {staged.map((entry, idx) => {
                        const tool = TOOLS.find(t => t.id === entry.tool_id);
                        const ToolIcon = tool?.icon;
                        const biz = businesses.find(b => b.id === entry.business_id);
                        const team = biz?.teams?.find(t => t.id === entry.team_id);
                        const bizName = biz?.name ?? entry.business_id;
                        const teamName = team?.name ?? entry.team_id;
                        const levelLabel = LEVEL_LABEL[entry.level] ?? entry.level;
                        const stripColor = entry.level === "w" ? WRITE_STRIP : entry.level === "r" ? READ_STRIP : entry.level === "*" ? ADMIN_STRIP : "bg-transparent";
                        const levelBadgeStyle = entry.level === "w"
                            ? "bg-violet-500 text-white"
                            : entry.level === "*"
                            ? "bg-purple-600 text-white"
                            : "bg-slate-500 text-white";
                        return (
                            <div key={idx} className="relative rounded-lg border border-border overflow-hidden flex flex-col h-[68px]">
                                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${stripColor}`} />
                                <button
                                    type="button"
                                    onClick={() => removeStagedEntry(entry.business_id, entry.team_id, entry.tool_id)}
                                    className="absolute top-1 right-1 rounded-full p-0.5 hover:bg-muted transition-colors text-muted-foreground"
                                >
                                    <X className="h-2.5 w-2.5" />
                                </button>
                                <div className="flex items-start gap-1.5 pl-2.5 pr-5 pt-1.5 pb-1 flex-1">
                                    {ToolIcon && (
                                        <div className={`h-5 w-5 rounded flex items-center justify-center shrink-0 ${tool?.iconBg ?? ""}`}>
                                            <ToolIcon className="h-2.5 w-2.5" />
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold leading-none truncate">{tool?.name ?? entry.tool_id}</p>
                                        <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{bizName} / {teamName}</p>
                                    </div>
                                </div>
                                <div className="px-2 pb-1.5 pt-0">
                                    <span className={`inline-flex items-center px-1.5 py-[2px] rounded text-[9px] font-semibold ${levelBadgeStyle}`}>
                                        {levelLabel}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        )}
        </>
    );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ManageAccess({
    permissions,
    decoded_permissions = [],
    user_req = [],
    availablePermissions: _availablePermissions,
    onUpdatePermission: _onUpdatePermission,
    onAddPermissions: _onAddPermissions,
    onRemovePermission,
    onCancelRequest,
    onDone,
    isProfile = false,
    userId,
    businessNameMapping = {},
    teamNameMapping = {},
    autoOpenDialog = false,
    defaultTeam,
}: ManageAccessProps) {
    const [isModalOpen, setIsModalOpen] = useState(autoOpenDialog);
    const [selectorMode, setSelectorMode] = useState<"custom" | "from_group">("custom");
    const [listSearchQuery, setListSearchQuery] = useState("");
    const [showAll, setShowAll] = useState(false);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [loadingBusinesses, setLoadingBusinesses] = useState(false);
    const [staged, setStaged] = useState<StagedEntry[]>([]);
    const [isSendingRequests, setIsSendingRequests] = useState(false);
    const [userMeId, setUserMeId] = useState<string | null>(null);
    const [groups, setGroups] = useState<GroupEntry[]>([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [groupSearch, setGroupSearch] = useState("");
    const [withdrawConfirmId, setWithdrawConfirmId] = useState<string | null>(null);
    const groupsFetchedRef = useRef(false);

    const isSuperAdmin = useMemo(() => hasWildcardAccess(permissions), [permissions]);

    const internalTeamNameMapping = useMemo(() => {
        const mapping: Record<string, string> = {};
        businesses.forEach(biz => {
            biz.teams?.forEach(team => { mapping[team.id] = team.name; });
        });
        return mapping;
    }, [businesses]);

    const combinedTeamNameMapping = useMemo(() => ({
        ...teamNameMapping,
        ...internalTeamNameMapping,
    }), [teamNameMapping, internalTeamNameMapping]);

    useEffect(() => {
        if (businesses.length === 0) {
            const fetchBusinesses = async () => {
                setLoadingBusinesses(true);
                try {
                    const response = await authApi.getTeams();
                    if (response.data) {
                        const validBusinesses = response.data.filter(
                            b => b.name?.trim() && b.status === 1 && b.id !== "*"
                        );
                        setBusinesses(validBusinesses);
                    }
                } catch {
                    toast.error("Failed to load businesses");
                } finally {
                    setLoadingBusinesses(false);
                }
            };

            const fetchUserMe = async () => {
                if (userId) { setUserMeId(userId); return; }
                try {
                    const response = await rpcApi.getUserMe() as RPCResponse<UserMeData> & { data?: { result?: UserMeData } };
                    const userData = response?.result || response?.data?.result;
                    if (userData) {
                        const id = userData.id || userData.user_id || userData._id;
                        if (id) setUserMeId(id);
                    }
                } catch { /* silent */ }
            };

            fetchBusinesses();
            fetchUserMe();
        }
    }, [businesses.length, userId]);

    useEffect(() => {
        if (autoOpenDialog) setIsModalOpen(true);
    }, [autoOpenDialog]);

    useEffect(() => {
        if (!isModalOpen || selectorMode !== "from_group" || groupsFetchedRef.current || loadingGroups) return;
        const fetchGroups = async () => {
            setLoadingGroups(true);
            try {
                const response = await rpcApi.listGroups();
                const result = (response as { result?: { count?: number; list?: GroupEntry[] } }).result;
                const list = result?.list;
                setGroups(Array.isArray(list) ? list : []);
                groupsFetchedRef.current = true;
            } catch {
                toast.error("Failed to load groups");
                setGroups([]);
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchGroups();
    }, [isModalOpen, selectorMode]);

    const parsedPermissions = useMemo<PermissionItem[]>(() => {
        const activeItems: PermissionItem[] = [];
        const pendingItems: PermissionItem[] = [];

        if (decoded_permissions && decoded_permissions.length > 0) {
            decoded_permissions.forEach(perm => {
                const level = mapAccessLevelToLevel(perm.access_level);
                const serviceLower = perm.service.toLowerCase();
                const matchingPerm = permissions.find(p => {
                    const parts = p.toLowerCase().split(".");
                    if (parts.length < 3) return false;
                    return parts[0] === perm.business.toLowerCase() &&
                        parts[1] === perm.team.toLowerCase() &&
                        (parts[2] === serviceLower || parts[2].includes(serviceLower));
                });

                const isFullWildcard = perm.business === "*" && perm.team === "*" &&
                    (perm.service === "*" || perm.service.toLowerCase() === "all") && level === "*";

                activeItems.push({
                    id: `${perm.business}.${perm.team}.${perm.service}.${level}`,
                    permission: matchingPerm || `${perm.business}.${perm.team}.${perm.service}.${level}`,
                    business: perm.business,
                    business_name: perm.business_name,
                    product: perm.team,
                    team_name: perm.team_name,
                    service: perm.service,
                    services: [perm.service],
                    level,
                    access_level: isFullWildcard ? "Super Admin" : perm.access_level,
                });
            });
        } else {
            permissions.forEach(perm => {
                if (perm === "*.*.*.*") {
                    activeItems.push({
                        id: perm, permission: perm,
                        business: "*", business_name: "All Business",
                        product: "*", team_name: "All Team",
                        service: "*", services: ["*"],
                        level: "*", access_level: "Super Admin",
                    });
                    return;
                }
                const parts = perm.split(".");
                const level = (parts[parts.length - 1] || "r") as PermissionLevel;
                const service = parts[parts.length - 2] || "*";
                const product = parts[parts.length - 3] || "*";
                const business = parts[parts.length - 4] || "*";
                activeItems.push({
                    id: perm, permission: perm,
                    business, business_name: businessNameMapping[business] || business,
                    product, team_name: teamNameMapping[product] || product,
                    service, services: [service],
                    level, access_level: LEVEL_LABEL[level],
                });
            });
        }

        user_req.forEach(req => {
            const parsed = parseServiceFromRequest(req.service);
            const serviceNames = parsed.map(p => p.name);
            // Use the highest level among all services in this request
            const level = parsed.some(p => p.level === "*") ? "*" as PermissionLevel
                : parsed.some(p => p.level === "w") ? "w" as PermissionLevel
                : "r" as PermissionLevel;
            const businessName = req.business_name || businessNameMapping[req.business] || req.business;
            const teamName = req.team_name || combinedTeamNameMapping[req.team] || teamNameMapping[req.team] || req.team;
            pendingItems.push({
                id: `req_${req.id}`,
                permission: `${req.business}.${req.team}.${serviceNames.join(",")}.${level}`,
                business: req.business, business_name: businessName,
                product: req.team, team_name: teamName,
                service: serviceNames.join(", "), services: serviceNames,
                level, access_level: LEVEL_LABEL[level],
                isRequestPending: true,
            });
        });

        return [...activeItems, ...pendingItems];
    }, [decoded_permissions, permissions, user_req, businessNameMapping, teamNameMapping, combinedTeamNameMapping]);

    const filteredPermissions = useMemo(() => {
        const query = listSearchQuery.trim();
        if (!query) return parsedPermissions;
        return parsedPermissions.filter(item => {
            const businessName = item.business_name || businessNameMapping[item.business] || item.business;
            const teamName = item.team_name || combinedTeamNameMapping[item.product] || teamNameMapping[item.product] || item.product;
            return matchesAccessFilter(businessName, teamName, item.service, query);
        });
    }, [parsedPermissions, listSearchQuery, businessNameMapping, teamNameMapping, combinedTeamNameMapping]);

    const visibleListings = useMemo(() =>
        showAll ? filteredPermissions : filteredPermissions.slice(0, SHOW_PERMISSION_COUNT),
        [filteredPermissions, showAll]
    );

    const formatItemDisplayName = useCallback((item: PermissionItem) => {
        const businessName = item.business_name || businessNameMapping[item.business] || item.business;
        const productName = item.team_name || combinedTeamNameMapping[item.product] || teamNameMapping[item.product] || item.product;
        // For clubbed requests with multiple services, format each service name
        if (item.services && item.services.length > 1) {
            const serviceLabels = item.services.map(svc => {
                if (svc === "*") return "All";
                const found = SERVICE_TYPES.find(s => s.value === svc.toLowerCase());
                return found?.label ?? svc;
            });
            return `${businessName === "*" ? "All Business" : businessName} - ${productName === "*" ? "All Team" : productName} - ${serviceLabels.join(", ")}`;
        }
        const serviceName = item.service === "*" ? "All" : item.service;
        return formatDisplayName(businessName, productName, serviceName);
    }, [businessNameMapping, teamNameMapping, combinedTeamNameMapping]);

    const getPermissionLevelLabel = useCallback((item: PermissionItem) => {
        const raw = item.access_level || LEVEL_LABEL[item.level] || "Read";
        return LEVEL_LABEL[mapAccessLevelToLevel(raw)];
    }, []);

    const handleRemoveFromList = useCallback(async (permissionId: string) => {
        if (onRemovePermission) {
            try {
                await onRemovePermission(permissionId);
                toast.success("Permission removed successfully");
            } catch {
                toast.error("Failed to remove permission");
            }
        }
    }, [onRemovePermission]);

    const handleCancelRequest = useCallback(async (requestId: string) => {
        try {
            await rpcApi.rejectServiceRequest(requestId);
            toast.success("Request withdrawn successfully");
            if (onCancelRequest) await onCancelRequest(requestId);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to reject request");
        }
    }, [onCancelRequest]);

    const handleSendRequests = useCallback(async () => {
        if (!userMeId) { toast.error("User ID not available"); return; }

        setIsSendingRequests(true);
        try {
            if (selectorMode === "from_group") {
                if (!selectedGroupId) { toast.error("No group selected"); return; }
                const group = groups.find(g => g.id === selectedGroupId);
                if (!group?.permissions?.length) { toast.error("Selected group has no permissions"); return; }

                // Build sets of already-held and pending permission keys for filtering
                const existingKeys = new Set(permissions.map(p => p.toLowerCase()));
                const pendingKeys = new Set(
                    user_req.map(req => {
                        const parsed = parseServiceFromRequest(req.service);
                        return parsed.map(p => `${req.business}.${req.team}.${p.name}.${p.level}`.toLowerCase());
                    }).flat()
                );

                // Parse group permissions and filter out already-held/pending
                const requests = group.permissions
                    .map(p => {
                        const parts = p.split(".");
                        const level = parts[parts.length - 1];
                        const svcId = parts[parts.length - 2];
                        const teamId = parts[parts.length - 3];
                        const bizId = parts[parts.length - 4];
                        return { business: bizId, team: teamId, service: `${svcId}.${level}`, id: userMeId, key: p.toLowerCase() };
                    })
                    .filter(r => !existingKeys.has(r.key) && !pendingKeys.has(r.key));

                if (requests.length === 0) {
                    toast.info("You already have all permissions in this group");
                    setIsSendingRequests(false);
                    return;
                }

                await profileApi.requestService(requests.map(({ key: _, ...r }) => r));
                toast.success(`${requests.length} access request${requests.length > 1 ? "s" : ""} submitted successfully`);
            } else {
                if (staged.length === 0) { toast.error("No permissions staged"); return; }
                const requests = staged.map(entry => ({
                    business: entry.business_id,
                    team: entry.team_id,
                    service: `${entry.tool_id}.${entry.level}`,
                    id: userMeId,
                }));
                await profileApi.requestService(requests);
                toast.success(`${staged.length} access request${staged.length > 1 ? "s" : ""} submitted successfully`);
                setStaged([]);
            }
            setIsModalOpen(false);
            if (onDone) await onDone();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to send requests");
        } finally {
            setIsSendingRequests(false);
        }
    }, [userMeId, selectorMode, selectedGroupId, groups, staged, onDone]);

    const handleModalCancel = useCallback(() => {
        setIsModalOpen(false);
        setTimeout(() => {
            setStaged([]);
            setSelectorMode("custom");
            setSelectedGroupId(null);
            setGroupSearch("");
            setGroups([]);
            groupsFetchedRef.current = false;
        }, 0);
    }, []);

    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setIsModalOpen(false);
            setTimeout(() => {
                setStaged([]);
                setSelectorMode("custom");
                setSelectedGroupId(null);
                setGroupSearch("");
                setGroups([]);
                groupsFetchedRef.current = false;
            }, 0);
        } else {
            setIsModalOpen(true);
        }
    }, []);

    return (
        <>
            <Card className="glass-card overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6 border-b">
                    <h3 className="text-base sm:text-lg font-semibold">Manage Access</h3>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Input
                            placeholder="Search by business, team or service..."
                            value={listSearchQuery}
                            onChange={e => setListSearchQuery(e.target.value.trimStart())}
                            className="w-full sm:w-64"
                        />
                        <Button
                            variant="secondary"
                            size="sm"
                            className="w-full sm:w-auto border border-border"
                            onClick={() => setIsModalOpen(true)}
                            disabled={isSuperAdmin && isProfile}
                        >
                            ADD SERVICE ACCESS
                        </Button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader className="bg-muted/50">
                            <TableRow>
                                <TableHead className="font-bold text-foreground text-xs sm:text-sm">ACCESS</TableHead>
                                <TableHead className="font-bold text-foreground text-xs sm:text-sm">ACCESS LEVEL</TableHead>
                                <TableHead className="font-bold text-foreground text-xs sm:text-sm w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredPermissions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                                        {listSearchQuery ? "No access permissions found matching your search" : "No access permissions found"}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleListings.map(item => {
                                    const isRequestPending = item.isRequestPending === true;
                                    return (
                                        <TableRow
                                            key={item.id}
                                            onMouseEnter={() => setHoveredId(item.id)}
                                            onMouseLeave={() => setHoveredId(null)}
                                            className={isRequestPending ? "bg-muted/30" : ""}
                                        >
                                            <TableCell className="font-medium py-4 text-xs sm:text-sm">
                                                {formatItemDisplayName(item)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {isRequestPending ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                                                                {getPermissionLevelLabel(item)}
                                                            </span>
                                                            <Badge variant="secondary" className="text-xs">Request Pending</Badge>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <span className="text-xs sm:text-sm font-medium">
                                                                {getPermissionLevelLabel(item)}
                                                            </span>
                                                            {!isProfile && hoveredId === item.id && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8"
                                                                    onClick={() => handleRemoveFromList(item.id)}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {isRequestPending && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => setWithdrawConfirmId(item.id.replace(/^req_/, ""))}
                                                        title="Withdraw request"
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
                {filteredPermissions.length > SHOW_PERMISSION_COUNT && (
                    <div className="flex justify-center p-4 border-t">
                        <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)}>
                            {showAll ? "Show Less" : "Read More"}
                        </Button>
                    </div>
                )}
            </Card>

            <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="max-w-[1100px] max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Add Service Access</DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto">
                        {loadingBusinesses ? (
                            <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span>Loading businesses...</span>
                            </div>
                        ) : (
                            <PermissionSelectorBody
                                businesses={businesses}
                                staged={staged}
                                onStagedChange={setStaged}
                                existingPermissions={permissions}
                                pendingRequests={user_req}
                                defaultTeam={defaultTeam}
                            />
                        )}
                    </div>

                    <DialogFooter className="border-t pt-4 flex items-center justify-between gap-2">
                        <div className="text-xs text-muted-foreground">
                            {staged.length > 0
                                ? `${staged.length} permission${staged.length > 1 ? "s" : ""} staged across all teams`
                                : "Select tools to stage access requests"}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={handleModalCancel} disabled={isSendingRequests}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSendRequests}
                                disabled={staged.length === 0 || isSendingRequests}
                            >
                                {isSendingRequests ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Send Request{staged.length > 1 ? "s" : ""}
                                    </>
                                )}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={withdrawConfirmId !== null} onOpenChange={(open) => { if (!open) setWithdrawConfirmId(null); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Withdraw Request</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">Are you sure you want to withdraw your request?</p>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setWithdrawConfirmId(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={async () => {
                                if (withdrawConfirmId) {
                                    await handleCancelRequest(withdrawConfirmId);
                                    setWithdrawConfirmId(null);
                                }
                            }}
                        >
                            Withdraw
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
